import { useState, useEffect, useRef, useMemo } from 'react';

import { __, curry, isEmpty, isNil, move, update, equals } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useAssetsPermissions from 'hooks/useAssetsPermissions';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { useSnackbar } from 'hooks/useSnackbar';
import { makeValidate } from 'hooks/useValidationSchema';
import { setRooms } from 'store/page';
import { getInitialRoomPositionsSelector, pageDataSelector } from 'store/page/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { isOwnerSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';

import { openUploadModal } from '../../../../../Layouts/Form/FormBuilder/Content/AddNewContent/uploadModal';
import { usePageContext } from '../../shared/context';
import { useSaveErrorHandler } from '../../shared/hooks/useSaveErrorHandler';
import { useHandleAfterNextDetails } from '../../utils';
import { baseValidationSchema } from './validationSchema';

export function useRoomForm({ room, page }) {
  const { withAssetSettings } = useAssetsPermissions();

  const [, setRoomName] = useState('');
  const dispatch = useDispatch();
  const isOwner = useSelector(isOwnerSelector);
  const validate = (page) => async (values) => {
    let errors = {};
    const { name } = values;

    if (
      page.rooms
        .filter((r) => r.id !== values.id)
        .map((r) => r.name)
        .includes(name)
    ) {
      errors.name = 'Room type must be unique';
    }

    const baseValidation = await makeValidate(baseValidationSchema)(values);
    errors = { ...errors, ...(baseValidation || {}) };

    return errors;
  };

  const onDelete = () => (asset, form) => {
    confirmAlert({
      title: 'Are you sure you want to delete this attached file?',
      confirmButtonText: 'Delete',
      onConfirm: () => {
        const files = [...(form?.getState()?.values?.media || [])];
        const index = files.findIndex((f) => f.id === asset.id);

        if (index > -1) {
          files.splice(index, 1);
        }

        form.change('media', files);
      },
    });
  };

  const onReorder = (form) => (oldIndex, newIndex) => {
    const files = [...(form?.getState()?.values?.media || [])];

    form.change('media', move(oldIndex, newIndex, files));
  };

  const openModal = async (type, form) => {
    const assets = await openUploadModal(dispatch, {
      type,
      suggested: page,
      selected: [...(form?.getState()?.values?.media || [])],
      modalTitle: 'Add Photos',
      isCenterOfImageEnabled: true,
      isSortingEnabled: true,
      isRoomSuggested: true,
      withAssetSettings,
      isOwner,

      defaults: {
        name: page.name,
        tags: [page.name, 'Room', 'Accommodations'],
      },
    });

    if (assets === null) {
      return;
    }

    form.change(type, assets);
  };

  const onRoomNameChange = (e) => {
    setRoomName(e.target.value);
  };

  return {
    data: room,
    onDelete,
    openModal,
    onReorder,
    validate,
    onRoomNameChange,
  };
}

export function useEditedRoom() {
  const { scrollToTop } = useScrollPageToTop();
  const dispatch = useDispatch();
  const page = useSelector(pageDataSelector);

  const { useNextStepHandler, useSavePageHandler } = usePageContext();
  const handleSaveError = useSaveErrorHandler({ needToRedirect: true });
  const onAfterNext = useHandleAfterNextDetails();

  // force update for synchronizing initial rooms positions with store
  const [forceUpdate, setForceUpdate] = useState(0);
  const handleNextStep = useNextStepHandler({
    onNextError: handleSaveError,
    onAfterNext: (page, params) => {
      setForceUpdate((f) => f + 1);
      onAfterNext(page, params);
    },
  });

  const save = useSavePageHandler({ onSaveError: handleSaveError });
  const onSnackbar = useSnackbar();
  const [isPendingRequest, setPendingRequest] = useState(false);
  const doesPageHaveChanges = useSelector(appHasChangesSelector);

  const initialRoomsPositions = useSelector(getInitialRoomPositionsSelector);
  const roomsPositions = useMemo(() => page.rooms.map((room) => room.id), [page.rooms]);
  const isRoomPositionsDirty = !equals(initialRoomsPositions, roomsPositions);

  useEffect(() => {
    dispatch(setAppHasChangesAction(isRoomPositionsDirty));
  }, [isRoomPositionsDirty, page.rooms, forceUpdate]);

  const handleSavePage = async (rooms) => {
    rooms = clearEmptyRooms(rooms);
    return await save({ ...page, rooms }, true);
  };

  const handleSave = async () => {
    return await save({ ...page }, true);
  };

  const handleAddNewRoom = async (values) => {
    if (isPendingRequest) {
      return;
    }

    setPendingRequest(true);
    let oldRooms = [...page.rooms];
    try {
      const newRooms = [...page.rooms, values];
      dispatch(setRooms(newRooms));
      await handleSavePage(newRooms);
      onAfterNext(page, { pageHadChangesBeforeSaving: true });

      scrollToTop();

      onSnackbar(`Room${getRoomName(values.name)}has been added.`);
    } catch (e) {
      console.error('---- handleAddNewRoom e', e);
      dispatch(setRooms(oldRooms));
      onSnackbar('Failed to save a room', true);
    } finally {
      setPendingRequest(false);
    }
  };

  const handleEditRoom = async (index, values) => {
    if (isPendingRequest) {
      return;
    }

    setPendingRequest(true);
    let oldRooms = [...page.rooms];
    try {
      const afterSaveWithHadChanges = curry(onAfterNext)(__, { pageHadChangesBeforeSaving: doesPageHaveChanges });
      const newRooms = update(index, values, page.rooms);
      dispatch(setRooms(newRooms));
      await handleSavePage(newRooms);
      afterSaveWithHadChanges(page);

      onSnackbar(`Room${getRoomName(values.name)}has been updated.`);
    } catch (e) {
      console.error('---- handleEditRoom e', e);
      dispatch(setRooms(oldRooms));
      onSnackbar('Failed to edit a room', true);
    } finally {
      setPendingRequest(false);
    }
  };

  const handleDeleteRoom = (index) => {
    return confirmAlert({
      title: 'Do you want to delete this room now?',
      onConfirm: async () => {
        setPendingRequest(true);
        let oldRooms = [...page.rooms];
        try {
          const newRoom = { ...page.rooms[index], _destroy: true };
          const newRooms = update(index, newRoom, page.rooms);
          dispatch(setRooms(newRooms));
          await handleSavePage(newRooms);
          onAfterNext(page, { pageHadChangesBeforeSaving: true });

          onSnackbar(`Room${getRoomName(newRoom.name)}has been deleted.`);
        } catch (e) {
          console.error('---- handleDeleteRoom e', e);
          dispatch(setRooms(oldRooms));
          onSnackbar('Failed to delete a room', true);
        } finally {
          setPendingRequest(false);
        }
      },
    });
  };

  const handleNext = async () => {
    await handleNextStep(page);
    onSnackbar(`Room${getRoomName(page.name)}has been added.`);
  };

  return {
    isPendingRequest,
    handleAddNewRoom,
    handleEditRoom,
    handleDeleteRoom,
    handleNext,
    handleSave,
  };
}

function clearEmptyRooms(rooms) {
  const filteredRooms = rooms.filter((r) => !isEmptyObject(r));

  return filteredRooms;
}

export function isRoomEmpty(values) {
  return isEmptyObject(values);
}

function isEmptyObject(obj) {
  return Object.keys(obj).every((key) => isNil(obj[key]) || isEmpty(obj[key]));
}

function getRoomName(name) {
  return name ? " '" + name + "' " : ' ';
}
